
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium border-0 focus:outline-none focus:ring-0 focus-visible:ring-0 disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        badge: "rounded-full text-sm font-semibold focus-visible:ring-0 user-select-none",
        
        // Grid view button variants
        gridButton: "bg-mono-antiflashWhite/30 text-mono-frenchGray2 hover:bg-mono-seasalt hover:text-mono-aurora rounded-full [&_svg]:size-6",
        gridButtonSelected: "bg-mono-seasalt text-mono-aurora rounded-full [&_svg]:size-6",
        gridButtonUndo: "bg-mono-antiflashWhite/30 text-mono-frenchGray2 hover:bg-mono-seasalt hover:text-mono-aurora rounded-full [&_svg]:size-6",
        
        // List view button variants - now matches grid/detail behavior (no transition classes)
        listButton: "bg-mono-onyx text-mono-seasalt hover:bg-mono-outerSpace rounded-full [&_svg]:size-5",
        listButtonSelected: "bg-mono-outerSpace text-mono-seasalt rounded-full [&_svg]:size-5",
        listButtonUndo: "bg-mono-onyx/30 text-mono-seasalt hover:bg-mono-outerSpace hover:text-mono-seasalt rounded-full [&_svg]:size-5",
        
        // Search result button variants - now matches grid/detail behavior (no transition classes)
        searchButton: "bg-mono-onyx text-mono-seasalt hover:bg-mono-outerSpace rounded-full [&_svg]:size-5",
        searchButtonSelected: "bg-mono-outerSpace text-mono-seasalt rounded-full [&_svg]:size-5",
        
        // Detail/dialog view button variants - fixed to match grid behavior
        detailButton: "bg-mono-antiflashWhite/30 text-mono-frenchGray2 hover:bg-mono-seasalt hover:text-mono-aurora rounded-full shadow-md [&_svg]:size-5",
        detailButtonSelected: "bg-mono-seasalt text-mono-aurora rounded-full shadow-md [&_svg]:size-5",
        detailButtonUndo: "bg-mono-antiflashWhite/30 text-mono-frenchGray2 hover:bg-mono-seasalt hover:text-mono-aurora rounded-full shadow-md [&_svg]:size-5",
        
        // Keep original variants for backward compatibility
        movieGhost: "bg-mono-antiflashWhite/30 text-mono-frenchGray2 hover:bg-mono-seasalt rounded-full",
        movieAction: "bg-mono-antiflashWhite/30 text-mono-frenchGray2 hover:bg-mono-seasalt rounded-full",
        movieActionSelected: "bg-mono-seasalt text-mono-aurora rounded-full",
        monoDark: "bg-mono-onyx text-mono-seasalt hover:bg-mono-outerSpace"
      },
      size: {
        default: "h-10 px-4 py-2 [&_svg]:size-4",
        sm: "h-9 rounded-md px-3 [&_svg]:size-4",
        lg: "h-11 rounded-md px-8 [&_svg]:size-5",
        icon: "h-10 w-10 [&_svg]:size-4",
        badge: "h-8 px-3 py-1.5 text-sm [&_svg]:size-3.5",
      },
      hovered: {
        true: "",
        false: ""
      },
      deselected: {
        true: "",
        false: ""
      }
    },
    compoundVariants: [
      // Grid hover states
      { variant: "gridButton", hovered: true, className: "group-hover:text-mono-aurora" },
      { variant: "gridButtonSelected", hovered: true, className: "group-hover:text-mono-aurora" },
      { variant: "gridButtonUndo", hovered: true, className: "group-hover:text-mono-aurora" },
      
      // Grid deselected states - now uses solid bg-mono-seasalt instead of opacity
      { variant: "gridButton", deselected: true, className: "bg-mono-seasalt text-mono-aurora" },
      
      // List hover states
      { variant: "listButton", hovered: true, className: "group-hover:text-mono-seasalt" },
      { variant: "listButtonSelected", hovered: true, className: "group-hover:text-mono-seasalt" },
      { variant: "listButtonUndo", hovered: true, className: "group-hover:text-mono-seasalt" },
      
      // List deselected states
      { variant: "listButtonSelected", deselected: true, className: "bg-mono-outerSpace text-mono-seasalt opacity-60" },
      
      // Search hover states
      { variant: "searchButton", hovered: true, className: "group-hover:text-mono-seasalt" },
      { variant: "searchButtonSelected", hovered: true, className: "group-hover:text-mono-seasalt" },
      
      // Search deselected states
      { variant: "searchButtonSelected", deselected: true, className: "bg-mono-outerSpace text-mono-seasalt opacity-60" },
      
      // Detail hover states - fixed to match grid behavior exactly
      { variant: "detailButton", hovered: true, className: "group-hover:text-mono-aurora" },
      { variant: "detailButtonSelected", hovered: true, className: "group-hover:text-mono-aurora" },
      { variant: "detailButtonUndo", hovered: true, className: "group-hover:text-mono-aurora" },
      
      // Detail deselected states - now matches grid exactly
      { variant: "detailButton", deselected: true, className: "bg-mono-seasalt text-mono-aurora" },
    ],
    defaultVariants: {
      variant: "default",
      size: "default",
      hovered: false,
      deselected: false,
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, hovered, deselected, asChild = false, type = "button", ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, hovered, deselected, className }))}
        type={type}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
